import {
  PolicySummary,
  PolicyType as CustomerPolicyType,
} from '@aaa-ncnu-ie/mypolicy-api-customers';
import { Stack, Card, Typography, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';

import AutoPayIcon from '../../../../assets/icons/AutoPay';
import ChevronRightIcon from '../../../../assets/icons/ChevronRight';
import CardHeader from '../../../../components/CardHeader';
import { DocumentChip } from '../../../../components/DocumentChip';
import InfoBlock from '../../../../components/InfoBlock';
import Button from '../../../../components/styledComponents/Button';
import { ContainedLink } from '../../../../components/styledComponents/Link';
import PhoneNumberLink from '../../../../components/styledComponents/PhoneNumberLink';
import colors from '../../../../constants/colors';
import useAnalytics from '../../../../hooks/useAnalytics';
import theme from '../../../../styles/theme';
import {
  policyTypeMap,
  policyTypeMapSelector,
} from '../../../../utils/policyTypeUtil';

interface PolicyCardProps {
  policy: PolicySummary;
}

const PolicyCard = ({
  policy: {
    policyType,
    policyTypeCd,
    policyNumber,
    policyDetails,
    policyEditable,
    policyStatus,
    nanoPolicy,
    fullProductCode,
    riskState,
  },
}: PolicyCardProps) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const { track } = useAnalytics();
  const isAutoPolicy = policyType === CustomerPolicyType.AUTO;
  const policyCancelled = policyStatus.toLowerCase().includes('cancel');
  const policyExpired = policyStatus === 'Expired';
  const isNYPolicy = isAutoPolicy && riskState === 'NY';
  const policyTitleIndex = policyTypeMapSelector(policyTypeCd, fullProductCode);
  const policyTitle = policyTypeMap[policyTitleIndex].title;

  const showIdCardButton = isAutoPolicy || policyType === CustomerPolicyType.MC;

  const handleViewDetailsClick = () => {
    track('View Details Accessed', {
      event_type: 'Link Accessed',
      location: 'Feature Card',
      link: 'View details',
      policy_number: policyNumber,
      state: riskState,
    });
  };

  const CardButtons = () => (
    <Stack
      alignItems="flex-end"
      direction={isMobile ? 'column' : 'row'}
      sx={{ ml: 0, my: { mobile: 2, tablet: 0 } }}
    >
      {policyEditable && (
        <ContainedLink
          to={`/edit/${policyNumber}`}
          onClick={() => {
            track('Edit Policy Accessed', {
              event_type: 'Link Accessed',
              location: 'Feature Card',
              link: 'Edit policy',
            });
          }}
          variantColor="white"
          sx={{
            mr: { tablet: 2 },
            mb: { mobile: 1, tablet: 0 },
            whiteSpace: 'nowrap',
          }}
          data-testid={`edit-policy-button-${policyNumber}`}
        >
          Edit policy
        </ContainedLink>
      )}
      <ContainedLink
        to={`/policy/${policyNumber}`}
        sx={{
          whiteSpace: 'nowrap',
        }}
        variantColor="blue"
        onClick={handleViewDetailsClick}
        data-testid={`view-policy-details-button-${policyNumber}`}
      >
        View details
      </ContainedLink>
    </Stack>
  );

  return (
    <Card
      component={Stack}
      sx={{
        boxShadow: '0px 4px 8px rgba(25, 30, 43, 0.08)',
        p: { tablet: 5, mobile: 2 },
        borderRadius: 2,
        height: 'auto',
      }}
    >
      <CardHeader
        cancelled={policyCancelled}
        expired={policyExpired}
        headerText={policyNumber.toUpperCase()}
        policyTypeCd={policyTypeCd}
        vehicles={policyDetails?.vehicles}
        insuredAddress={policyDetails?.insuredAddress}
        isNanoPolicy={nanoPolicy}
        policyNumber={policyNumber}
        fullProductCode={fullProductCode}
      />
      <Stack
        direction="row"
        sx={{
          mt: { mobile: 2, tablet: 3 },
        }}
        justifyContent="space-between"
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { desktop: 44, tablet: 36, mobile: 33 },
            lineHeight: '57px',
            color: colors.csaaBlack,
          }}
        >
          {policyTitle} policy
        </Typography>
        {!isMobile && <CardButtons />}
      </Stack>
      {isMobile && <CardButtons />}
      {isNYPolicy && (
        <Stack
          data-testid="idCardAvailabilityInfoBar"
          sx={{ mt: { mobile: 1, tablet: 3 }, mb: { mobile: 2, tablet: 0 } }}
        >
          <InfoBlock
            indent
            text={
              <Stack>
                <Typography
                  sx={{
                    fontSize: { mobile: 16, tablet: 18 },
                    fontWeight: 400,
                    color: colors.csaaMid,
                    lineHeight: '24px',
                    fontFamily: 'Cabin',
                    fontKerning: 'normal',
                  }}
                >
                  Insurance ID cards for your state may not be available online.
                  To get ID cards, call Service at{' '}
                  <PhoneNumberLink
                    phoneNumber="18889805650"
                    formattedPhoneNumber="888.980.5650"
                  />
                </Typography>
              </Stack>
            }
          />
        </Stack>
      )}
      {showIdCardButton && !policyCancelled && (
        <Stack
          direction={isMobile ? 'column-reverse' : 'row'}
          spacing={isMobile ? 2 : 4}
          sx={{
            mt: { mobile: isNYPolicy ? 0 : 2, tablet: isNYPolicy ? 3 : 5 },
          }}
          justifyContent="space-between"
          alignItems={!isMobile ? 'center' : ''}
        >
          <DocumentChip
            labelText="ID Card"
            sx={{ width: '94px' }}
            policyNumber={policyNumber}
            policyType={policyType}
            onClick={() => {
              track('Document Downloaded', {
                event_type: 'File Downloaded',
                document_name: 'Identification Card',
                location: 'Feature Card',
                link: 'Identification Card',
              });
            }}
            testId={`id-card-chip-${policyNumber}`}
          />
          {policyEditable && (
            <Button
              component={Link}
              to={`/edit/${policyNumber}`}
              disableFocusRipple
              disableRipple
              startIcon={!isMobile && <AutoPayIcon height={16} width={16} />}
              endIcon={
                isMobile && <ChevronRightIcon sx={{ height: 16, width: 16 }} />
              }
              sx={{
                color: colors.csaaBlack,
                px: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: ['flex-start', null, null],
              }}
              variant="text"
              onClick={() => {
                track('Edit Policy Accessed', {
                  event_type: 'Link Accessed',
                  location: 'Feature Card',
                  link: 'Quote policy changes',
                });
              }}
              data-testid={`quote-policy-changes-button-${policyNumber}`}
              id={`quote-policy-changes-link-${policyNumber}`}
            >
              Quote policy changes
            </Button>
          )}
        </Stack>
      )}
    </Card>
  );
};

export default PolicyCard;
